import React, { useState, useCallback, useEffect } from "react";
import {
  FaUser,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { createUser, fetchUserByUsername, deleteUser } from "../../../api";
import { ROLE_MAPPING } from "../../constants";
import "../ProfileDetail/ProfileDetail.css";

// Constants for Doctor/RA form
const TABS = [
  { id: "personal", label: "Personal Information", icon: FaUser },
];

const INITIAL_FORM_DATA = {
  full_name: "",
  email: "",
  phone: "",
  password: "",
  reenter_password: "",
  organization_name: "",
};

const VALIDATION_RULES = {
  full_name: { required: true, minLength: 2, maxLength: 100 },
  email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  phone: { required: true, pattern: /^[\+]?[1-9][\d]{0,15}$/ },
  password: { required: true, minLength: 8 },
  reenter_password: { required: true },
  organization_name: { required: false }, // Made conditionally required based on userType
};

// Utility Functions
const useFormValidation = (formData) => {
  const [errors, setErrors] = useState({});

  const validateField = useCallback(
    (name, value) => {
      const rule = VALIDATION_RULES[name];
      if (!rule) return true;

      let isValid = true;
      let errorMessage = null;

      if (rule.required && !value?.toString().trim()) {
        errorMessage = "This field is required";
        isValid = false;
      } else if (
        value &&
        rule.pattern &&
        !rule.pattern.test(value.toString().trim())
      ) {
        errorMessage = getPatternErrorMessage(name);
        isValid = false;
      } else if (
        value &&
        rule.minLength &&
        value.toString().length < rule.minLength
      ) {
        errorMessage = `Must be at least ${rule.minLength} characters`;
        isValid = false;
      } else if (
        value &&
        rule.maxLength &&
        value.toString().length > rule.maxLength
      ) {
        errorMessage = `Must be no more than ${rule.maxLength} characters`;
        isValid = false;
      }

      setErrors(prev => {
        const newErrors = { ...prev };
        if (errorMessage) {
          newErrors[name] = errorMessage;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });

      return isValid;
    },
    []
  );

  const validatePasswordMatch = useCallback(() => {
    if (formData.password && formData.reenter_password) {
      if (formData.password !== formData.reenter_password) {
        setErrors(prev => ({...prev, reenter_password: "Passwords do not match"}));
        return false;
      } else {
        setErrors(prev => {
          const newErrors = {...prev};
          delete newErrors.reenter_password;
          return newErrors;
        });
        return true;
      }
    }
    return true;
  }, [formData.password, formData.reenter_password]);

  const validatePasswordMatchWithValues = useCallback((password, reenterPassword) => {
    if (password && reenterPassword) {
      if (password !== reenterPassword) {
        setErrors(prev => ({...prev, reenter_password: "Passwords do not match"}));
        return false;
      } else {
        setErrors(prev => {
          const newErrors = {...prev};
          delete newErrors.reenter_password;
          return newErrors;
        });
        return true;
      }
    } else {
      // Clear error if one of the fields is empty
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.reenter_password;
        return newErrors;
      });
    }
    return true;
  }, []);

  const getPatternErrorMessage = (fieldName) => {
    const messages = {
      email: "Please enter a valid email address",
      phone: "Please enter a valid phone number",
    };
    return messages[fieldName] || "Invalid format";
  };

  const validateAll = useCallback((userType) => {
    const requiredFields = Object.keys(VALIDATION_RULES).filter(
      (key) => {
        // Organization name is only required for institution_owner
        if (key === 'organization_name') {
          return userType === 'institutionowner';
        }
        return VALIDATION_RULES[key].required;
      }
    );

    let isValid = true;

    requiredFields.forEach((field) => {
      if (!validateField(field, formData[field] || "")) {
        isValid = false;
      }
    });

    // Validate password match
    if (!validatePasswordMatch()) {
      isValid = false;
    }

    return isValid;
  }, [formData, validateField, validatePasswordMatch]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validateField,
    validateAll,
    clearErrors,
    validatePasswordMatch,
    validatePasswordMatchWithValues,
    setErrors,
  };
};

const useFormData = (initialData = INITIAL_FORM_DATA) => {
  const [formData, setFormData] = useState(initialData);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialData);
    setHasChanges(false);
  }, [initialData]);

  const setFormDataDirectly = useCallback((newData) => {
    setFormData(newData);
    setHasChanges(false);
  }, []);

  return {
    formData,
    handleChange,
    resetForm,
    setFormDataDirectly,
    hasChanges,
  };
};

// UI Components
const TabNavigation = ({ activeTab, onTabChange }) => (
  <div className="row-view">
    {TABS.map((tab) => {
      const IconComponent = tab.icon;
      const isActive = activeTab === tab.id;
      return (
        <div
          key={tab.id}
          className={`button ${isActive ? "button-active" : ""}`}
          onClick={() => onTabChange(tab.id)}
        >
          <div className="row-view2">
            <IconComponent className="image" />
            <span className="text3">{tab.label}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const InputField = ({
  label,
  name,
  value = "",
  onChange,
  placeholder,
  type = "text",
  error,
  required = false,
  className = "",
  disabled = false,
  rows,
  ...props
}) => {
  const isTextArea = type === "textarea";
  const InputComponent = isTextArea ? "textarea" : "input";
  return (
    <div className={`column ${className}`}>
      {label && (
        <div className="text2">
          {label} {required && <span className="required-text">*</span>}
        </div>
      )}
      <InputComponent
        type={isTextArea ? undefined : type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`input ${error ? "input-error" : ""} ${
          disabled ? "input-disabled" : ""
        }`}
        required={required}
        disabled={disabled}
        rows={rows}
        {...props}
      />
      {error && <div className="error-text">{error}</div>}
    </div>
  );
};

const PersonalInfoForm = ({ formData, onChange, errors, userType = "doctor" }) => (
  <div className="column2">
    <InputField
      label="Full Name"
      name="full_name"
      value={formData.full_name}
      onChange={onChange}
      placeholder="Enter full name"
      error={errors.full_name}
      required
      className="column4"
    />

    <div className="row-view4">
      <InputField
        label="Email Address"
        name="email"
        value={formData.email}
        onChange={onChange}
        placeholder="Enter email address"
        type="email"
        error={errors.email}
        required
        className="column4"
      />
      <InputField
        label="Phone Number"
        name="phone"
        value={formData.phone}
        onChange={onChange}
        placeholder="Enter phone number"
        type="tel"
        error={errors.phone}
        required
        className="column4"
      />
    </div>

    {userType === "institutionowner" && (
      <InputField
        label="Organization Name"
        name="organization_name"
        value={formData.organization_name}
        onChange={onChange}
        placeholder="Enter organization name"
        error={errors.organization_name}
        required
        className="column4"
      />
    )}

    <div className="row-view4">
      <InputField
        label="Password"
        name="password"
        value={formData.password}
        onChange={onChange}
        placeholder="Enter password"
        type="password"
        error={errors.password}
        required
        className="column4"
      />
      <InputField
        label="Re-enter Password"
        name="reenter_password"
        value={formData.reenter_password}
        onChange={onChange}
        placeholder="Re-enter password"
        type="password"
        error={errors.reenter_password}
        required
        className="column4"
      />
    </div>
  </div>
);

const DeleteConfirmModal = ({ label, onConfirm, onCancel, isDeleting }) => (
  <div className="modal-overlay">
    <div className="modal-box">
      <h3 className="modal-title">Delete {label}</h3>
      <p className="modal-body">
        Are you sure you want to permanently delete this {label.toLowerCase()}? This action
        <strong> cannot be undone</strong>.
      </p>
      <div className="modal-actions">
        <div className="button2" onClick={onCancel} style={{ opacity: isDeleting ? 0.5 : 1, pointerEvents: isDeleting ? "none" : "auto" }}>
          <FaTimes className="image" />
          <span className="text3">Cancel</span>
        </div>
        <div
          className="button-delete"
          onClick={!isDeleting ? onConfirm : undefined}
          style={{ opacity: isDeleting ? 0.5 : 1, pointerEvents: isDeleting ? "none" : "auto" }}
        >
          <FaTrash className="image" />
          <span className="text3">{isDeleting ? "Deleting..." : "Yes, Delete"}</span>
        </div>
      </div>
    </div>
  </div>
);

const ActionButtons = ({
  onSave,
  onCancel,
  onDelete,
  hasChanges,
  isSubmitting,
  mode = "create",
  userType = "doctor",
  isFormValid = true,
}) => {
  const getUserTypeLabel = () => {
    const labels = {
      doctor: "Doctor",
      ra: "RA",
      labeler: "Labeler",
      institutionowner: "Institution Owner",
      admin: "Admin",
      parents: "Parent",
    };
    return labels[userType] || "User";
  };

  return (
    <div className="button-row-view">
      <div
        className="button2"
        onClick={onCancel}
        style={{
          opacity: isSubmitting ? 0.5 : 1,
          pointerEvents: isSubmitting ? "none" : "auto",
        }}
      >
        <FaTimes className="image" />
        <span className="text3">Cancel</span>
      </div>
      {mode === "edit" && onDelete && (
        <div
          className="button-delete"
          onClick={!isSubmitting ? onDelete : undefined}
          style={{ opacity: isSubmitting ? 0.5 : 1, pointerEvents: isSubmitting ? "none" : "auto" }}
        >
          <FaTrash className="image" />
          <span className="text3">Delete</span>
        </div>
      )}
      <div
        className={`button3 ${
          !isFormValid || isSubmitting ? "button-disabled" : ""
        }`}
        onClick={!isFormValid || isSubmitting ? undefined : onSave}
        style={{ opacity: !isFormValid || isSubmitting ? 0.5 : 1 }}
      >
        <FaSave className="image" />
        <span className="text3">
          {isSubmitting
            ? "Saving..."
            : mode === "create"
            ? `Create ${getUserTypeLabel()}`
            : "Save Changes"}
        </span>
      </div>
    </div>
  );
};

const SuccessMessage = ({ message, onClose }) => (
  <div className="success-message">
    <span>{message}</span>
    <FaTimes className="close-icon" onClick={onClose} />
  </div>
);

const ErrorMessage = ({ message, onClose }) => (
  <div className="error-message">
    <span>{message}</span>
    <FaTimes className="close-icon" onClick={onClose} />
  </div>
);

// Main Component
const UserProfileDetail = ({
  mode = "create",
  userId = null,
  userType = "doctor", // "doctor" or "ra"
  onSave,
  onCancel,
  onDelete,
  currentUserProfile = null,
}) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  const { formData, handleChange, resetForm, setFormDataDirectly, hasChanges } =
    useFormData(INITIAL_FORM_DATA);

  const {
    errors,
    validateField,
    validateAll,
    clearErrors,
    validatePasswordMatch,
    validatePasswordMatchWithValues,
  } = useFormValidation(formData);

  useEffect(() => {
    if (mode === "edit" && userId) {
      loadUserData();
    }
  }, [mode, userId]);

  const loadUserData = async () => {
    setIsLoading(true);
    try {
      const userData = await fetchUserByUsername(userId);
      setCurrentUserEmail(userData.email || userId);
      setFormDataDirectly({
        full_name: userData.full_name || "",
        email: userData.email || "",
        phone: "", // Not in API response, leave empty
        password: "",
        reenter_password: "",
        organization_name: userData.metadata?.organizationName || "",
      });
      console.log(`Loaded ${userType} data:`, userData);
    } catch (error) {
      console.error(`Error loading ${userType} data:`, error);
      setErrorMessage(`Failed to load ${userType} data`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    validateField(name, value);
    
    // If changing password fields, validate match with the new value
    if (name === "password") {
      // Password changed, check against current reenter_password
      validatePasswordMatchWithValues(value, formData.reenter_password);
    } else if (name === "reenter_password") {
      // Reenter password changed, check against current password
      validatePasswordMatchWithValues(formData.password, value);
    }
  };

  const isFormValid = () => {
    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Check required fields based on userType
    const requiredFields = ['full_name', 'email', 'phone', 'password', 'reenter_password'];
    
    // Add organization_name if userType is institutionowner
    if (userType === 'institutionowner') {
      requiredFields.push('organization_name');
    }

    // Check if all required fields are filled
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        return false;
      }
    }

    // Check if passwords match
    if (formData.password !== formData.reenter_password) {
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateAll(userType)) {
      setErrorMessage("Please fill in all required fields correctly");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (mode === "create") {
        // Map userType to platform_role
        const userData = {
          email: formData.email,
          full_name: formData.full_name,
          platform_role: ROLE_MAPPING[userType] || userType,
          profile_permissions: [],
          password: formData.password,
          metadata: {},
        };

        // Add assigned_to for RA role
        if (userType === "ra" && currentUserProfile?.username) {
          userData.assigned_to = currentUserProfile.username;
        }

        // Add organization name to metadata if userType is institution_owner
        if (userType === "institutionowner" && formData.organization_name) {
          userData.metadata.organizationName = formData.organization_name;
        }

        const response = await createUser(userData);
        console.log(`${userType} created successfully:`, response);
        
        const userTypeLabel = {
          doctor: "Doctor",
          ra: "RA",
          labeler: "Labeler",
          institutionowner: "Institution Owner",
          admin: "Admin",
          parents: "Parent",
        }[userType] || "User";
        
        setSuccessMessage(`${userTypeLabel} created successfully!`);
        
        if (onSave) {
          setTimeout(() => {
            onSave(response);
          }, 1500);
        }
      } else {
        // TODO: Implement update user API call
        console.log(`Updating ${userType}:`, formData);
        setSuccessMessage(`${userType} updated successfully!`);
        
        if (onSave) {
          setTimeout(() => {
            onSave(formData);
          }, 1500);
        }
      }
    } catch (error) {
      console.error(`Error saving ${userType}:`, error);
      let errorMsg = `Failed to ${mode === "create" ? "create" : "update"} ${userType}`;
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail
            .map((err) => err.msg || err.message || err)
            .join(", ");
        } else {
          errorMsg = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const usernameToDelete = currentUserEmail || userId;
    if (!usernameToDelete) return;
    setIsDeleting(true);
    try {
      await deleteUser(usernameToDelete);
      setShowDeleteModal(false);
      onDelete?.();
    } catch (error) {
      setShowDeleteModal(false);
      let errorMsg = "Failed to delete user";
      if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === "string"
          ? error.response.data.detail
          : error.response.data.detail.map((e) => e.msg || e).join(", ");
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmCancel = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmCancel) return;
    }
    resetForm();
    clearErrors();
    if (onCancel) {
      onCancel();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "personal":
        return (
          <PersonalInfoForm
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            userType={userType}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="profile-detail-container">
        <div className="loading-message">Loading {userType} data...</div>
      </div>
    );
  }

  const userTypeLabel = {
    doctor: "Doctor",
    ra: "RA",
    labeler: "Labeler",
    institutionowner: "Institution Owner",
    admin: "Admin",
  }[userType] || "User";

  return (
    <div className="profile-detail-container">
      {showDeleteModal && (
        <DeleteConfirmModal
          label={userTypeLabel}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="form-content">{renderTabContent()}</div>

      <ActionButtons
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={mode === "edit" ? handleDeleteClick : undefined}
        hasChanges={hasChanges}
        isSubmitting={isSubmitting}
        mode={mode}
        userType={userType}
        isFormValid={isFormValid()}
      />
    </div>
  );
};

export default UserProfileDetail;
